import { RefObject, useLayoutEffect } from 'react'
import { Theme } from '@mui/material'
import { IPosition, IPositionType } from 'src/generated-types'
import { IMooringType } from 'src/generated-types/mooring-type'
import { IChangeType } from 'src/generated-types/project-version-changes'
import { ConfigurePalette } from '../components/positions/configure/configurePalette'
import { getChangeColor, publishedNameOf } from './projectVersionChanges.action'
import { RevisionConfigure } from './useRevisionConfigure'
import { paintRevisionDrawing } from './revisionDrawingPainter'
import { drawAddLineMarkers, perimeterCorners } from './revisionMapOverlay'

interface UseRevisionSvgPaintOptions {
  hostRef: RefObject<HTMLDivElement>
  svg: string | undefined
  positions: IPosition[]
  activeType: IPositionType
  selectedIds: Set<number>
  palette: ConfigurePalette
  theme: Theme
  revision?: RevisionConfigure
  // Positions of a hovered drawing card; while set, configured positions keep the card highlight.
  highlightedIds: Set<number>
  configById: ReadonlyMap<number, unknown>
  // The map is painted again when these change, so the highlight has to be drawn over the fresh paint.
  repaintTriggers: unknown[]
}

const KINDS = [IChangeType.Added, IChangeType.Modified, IChangeType.Moved, IChangeType.Removed]

// Colours the draft's changes on the map once the dialog's own painting is done, and draws the Add line markers.
export const useRevisionSvgPaint = ({
  hostRef,
  svg,
  positions,
  activeType,
  selectedIds,
  palette,
  theme,
  revision,
  highlightedIds,
  configById,
  repaintTriggers,
}: UseRevisionSvgPaintOptions) => {
  const isAfter = !!revision && revision.view === 'after'
  const highlight = !!revision?.highlight
  const changeByName = revision?.changeByName
  const removedByName = revision?.removedByName
  const baseSvg = revision?.baseSvg
  const basePositions = revision?.basePositions
  const selectedRemoved = revision?.selectedRemoved
  const tool = revision?.tool
  const project = revision?.project

  useLayoutEffect(() => {
    const host = hostRef.current
    const svgElement = host?.querySelector('svg')
    if (!isAfter || !svg || !svgElement || !changeByName) return
    const colors = Object.fromEntries(KINDS.map(kind => [kind, getChangeColor(theme, kind)])) as Record<
      IChangeType,
      string
    >
    if (highlight) {
      paintRevisionDrawing(svgElement, {
        positions,
        changeByName: new Map([...changeByName].map(([name, change]) => [name, change.type])),
        removedNames: new Set(removedByName?.keys()),
        colors,
        palette,
        base: baseSvg && basePositions ? { svg: baseSvg, positions: basePositions } : undefined,
        activeType,
        skipIds: highlightedIds.size > 0 ? new Set([...selectedIds, ...configById.keys()]) : selectedIds,
        selectedRemoved: new Set(selectedRemoved),
        movedNames: new Set(
          [...changeByName].filter(([, change]) => change.kinds.includes(IChangeType.Moved)).map(([name]) => name),
        ),
        ghostColor: palette.unconfigured,
        publishedNameOf: name => publishedNameOf(changeByName, name),
      })
    }
    if (tool === 'addLine') {
      const corners = positions.filter(p => p.type === IPositionType.Corner)
      const allowed = project?.type === IMooringType.Barge ? corners : perimeterCorners(corners, project?.noOfRows ?? 0)
      drawAddLineMarkers(svgElement, allowed, colors[IChangeType.Added])
    }
  }, [
    hostRef,
    isAfter,
    highlight,
    svg,
    positions,
    changeByName,
    removedByName,
    baseSvg,
    basePositions,
    selectedRemoved,
    tool,
    project,
    activeType,
    selectedIds,
    highlightedIds,
    configById,
    palette,
    theme,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...repaintTriggers,
  ])
}
